"use client";

import React from "react";
import { Check, Star } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const useWvcLanguage = () => {
  try {
    return (window as any)?.__WVC_LANG__ || "en";
  } catch {
    return "en";
  }
};
const translations = {
  en: {
    heading: "What our clients say",
    worn: "Worn &",
    loved: "loved",
    worldwide: "worldwide",
    verified: "Verified",
    purchasedLabel: "Purchased:",
    deliveredText: "Over 10,000 tailored garments delivered across Europe and North America",
    emmaQuote: "The quality is unmatched. Every detail speaks to the craftsmanship.",
    sofiaQuote: "I love how versatile these pieces are—equally stunning dressed up or down.",
    oliviaQuote: "The fit is perfect, and the fabrics feel luxurious without being pretentious.",
    emmaLocation: "London, UK",
    sofiaLocation: "New York, US",
    oliviaLocation: "Paris, FR",
    emmaPurchase: "Structured Wool Overcoat",
    sofiaPurchase: "Ribbed Mulberry Silk Set",
    oliviaPurchase: "Pleated Column Dress"
  },
  zh: {
    heading: "我们的客户说什么",
    worn: "穿着和",
    loved: "爱",
    worldwide: "全球",
    verified: "验证",
    purchasedLabel: "购买:",
    deliveredText: "超过10,000件定制服装已交付到欧洲和北美",
    emmaQuote: "质量无与伦比。每个细节都说明了工艺。",
    sofiaQuote: "我喜欢这些件的多功能性——打扮或打扮时同样令人惊艳。",
    oliviaQuote: "合身完美，面料感到奢华而不自大。",
    emmaLocation: "伦敦，英国",
    sofiaLocation: "纽约，美国",
    oliviaLocation: "巴黎，法国",
    emmaPurchase: "结构化羊毛大衣",
    sofiaPurchase: "罗纹桑蚕丝套装",
    oliviaPurchase: "褶皱柱形连衣裙"
  },
  fr: {
    heading: "Ce que disent nos clients",
    worn: "Porté et",
    loved: "aimé",
    worldwide: "mondial",
    verified: "Vérifiée",
    purchasedLabel: "Acheté:",
    deliveredText: "Plus de 10 000 vêtements taillés livrés en Europe et en Amérique du Nord",
    emmaQuote: "La qualité est inégalée. Chaque détail parle de l'artisanat.",
    sofiaQuote: "J'aime la polyvalence de ces pièces—tout aussi étonnantes habillées ou déshabillées.",
    oliviaQuote: "L'ajustement est parfait et les tissus se sentent luxueux sans être prétentieux.",
    emmaLocation: "Londres, Royaume-Uni",
    sofiaLocation: "New York, États-Unis",
    oliviaLocation: "Paris, France",
    emmaPurchase: "Pardessus en Laine Structuré",
    sofiaPurchase: "Ensemble de Soie de Mûrier Côtelé",
    oliviaPurchase: "Robe Colonne Plissée"
  },
  es: {
    heading: "Lo que dicen nuestros clientes",
    worn: "Usado y",
    loved: "amado",
    worldwide: "mundial",
    verified: "Verificado",
    purchasedLabel: "Comprado:",
    deliveredText: "Más de 10,000 prendas personalizadas entregadas en Europa y América del Norte",
    emmaQuote: "La calidad es incomparable. Cada detalle habla de la artesanía.",
    sofiaQuote: "Me encanta lo versátiles que son estas piezas—igualmente impresionantes vestidas o sin vestir.",
    oliviaQuote: "El ajuste es perfecto y los tejidos se sienten lujosos sin ser pretenciosos.",
    emmaLocation: "Londres, Reino Unido",
    sofiaLocation: "Nueva York, EE.UU.",
    oliviaLocation: "París, Francia",
    emmaPurchase: "Abrigo de Lana Estructurado",
    sofiaPurchase: "Conjunto de Seda de Morera Acanalado",
    oliviaPurchase: "Vestido Columna Plisado"
  },
  de: {
    heading: "Was unsere Kunden sagen",
    worn: "Getragen und",
    loved: "geliebt",
    worldwide: "weltweit",
    verified: "Verifiziert",
    purchasedLabel: "Gekauft:",
    deliveredText: "Über 10.000 maßgeschneiderte Kleidungsstücke in Europa und Nordamerika geliefert",
    emmaQuote: "Die Qualität ist unvergleichlich. Jedes Detail spricht für die Handwerkskunst.",
    sofiaQuote: "Mir gefällt, wie vielseitig diese Teile sind – gleich beeindruckend angezogen oder abgelegt.",
    oliviaQuote: "Der Sitz ist perfekt, und die Stoffe fühlen sich luxuriös an, ohne anmaßend zu wirken.",
    emmaLocation: "London, Vereinigtes Königreich",
    sofiaLocation: "New York, USA",
    oliviaLocation: "Paris, Frankreich",
    emmaPurchase: "Strukturierter Wollmantel",
    sofiaPurchase: "Gerippter Maulbeerseiden-Set",
    oliviaPurchase: "Gefaltetes Säulenkleid"
  }
};
interface ReviewItem {
  id: number;
  author: string;
  location: string;
  quote: string;
  itemPurchased: string;
  rating: number;
}
export default function Testimonials() {
  const shouldReduceMotion = useReducedMotion();
  const rawLang = useWvcLanguage();
  const language = (rawLang in translations ? rawLang : "en") as keyof typeof translations;
  const t = translations[language];
  const reviewsData: ReviewItem[] = [{
    id: 1,
    author: "Emma L.",
    location: t.emmaLocation,
    quote: t.emmaQuote,
    itemPurchased: t.emmaPurchase,
    rating: 5
  }, {
    id: 2,
    author: "Sofia M.",
    location: t.sofiaLocation,
    quote: t.sofiaQuote,
    itemPurchased: t.sofiaPurchase,
    rating: 5
  }, {
    id: 3,
    author: "Olivia P.",
    location: t.oliviaLocation,
    quote: t.oliviaQuote,
    itemPurchased: t.oliviaPurchase,
    rating: 5
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="用户评价" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="container mx-auto max-w-[1360px] px-6 md:px-10 lg:px-16">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <p className="font-sans text-xs md:text-sm font-semibold tracking-widest text-primary uppercase mb-3">
            {t.heading}
          </p>
          <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight text-foreground leading-tight">
            {t.worn} <span className="italic font-light">{t.loved}</span> {t.worldwide}
          </h2>
          <div className="w-12 h-px bg-border mx-auto mt-5" />
        </div>

        {/* Testimonials 3-Column Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-40px"
      }}>
          {reviewsData.map((item, i) => <div data-index={i} key={item.id} className="h-full">
              <motion.div variants={cardVariants} className="h-full" whileHover={shouldReduceMotion ? undefined : {
            y: -3,
            transition: {
              duration: 0.2
            }
          }}>
                <Card className="h-full bg-card text-card-foreground border border-border rounded-none shadow-none p-6 md:p-8 flex flex-col justify-between transition-colors">
                  <CardContent className="p-0 flex flex-col justify-between h-full space-y-6">
                    <div>
                      {/* 5-Star Rating & Verified Badge Row */}
                      <div className="flex items-center justify-between gap-2 mb-5">
                        <div className="flex items-center gap-1 text-accent" aria-label={`Rating: ${item.rating} out of 5 stars`}>
                          {Array.from({
                        length: item.rating
                      }).map((_, starIdx) => <Star key={starIdx} className="size-4 fill-accent text-accent" />)}
                        </div>
                        <Badge variant="outline" className="rounded-none border-border bg-background text-[11px] font-medium text-muted-foreground px-2 py-0.5 gap-1 tracking-wide">
                          <Check className="size-3 text-primary" />
                          {t.verified}
                        </Badge>
                      </div>

                      {/* Editorial Customer Quote */}
                      <blockquote className="text-foreground font-sans text-sm md:text-base leading-relaxed tracking-normal">
                        "{item.quote}"
                      </blockquote>
                    </div>

                    {/* Author Metadata */}
                    <div className="pt-5 border-t border-border mt-auto">
                      <div className="flex items-baseline justify-between">
                        <h3 className="font-serif text-lg font-medium text-foreground tracking-tight">
                          {item.author}
                        </h3>
                        <span className="font-sans text-xs text-muted-foreground">
                          {item.location}
                        </span>
                      </div>
                      <p className="font-sans text-xs text-muted-foreground mt-1">
                        {t.purchasedLabel}{" "}
                        <span className="text-foreground/80 font-medium">
                          {item.itemPurchased}
                        </span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>)}
        </motion.div>

        {/* Editorial Sub-Footer Note */}
        <div className="text-center mt-12 md:mt-14">
          <p className="font-sans text-xs text-muted-foreground tracking-wide">
            {t.deliveredText}
          </p>
        </div>
      </div>
    </section>;
}